import * as shared from './shared';
import { BrandLogoColorBackgroundSpecific, ITheme, IThemeColors } from '.';
import { css } from 'styled-components';

interface Colors extends IThemeColors {
  border: string;
}

const colors: Colors = {
  white: '#ffffff',
  grey100: '#f5f5f5',
  grey200: '#eaeaea',
  grey300: '#d8d8d8',
  grey400: '#cccccc',
  grey450: '#979797',
  grey500: '#696969',
  grey600: '#cbdbf1',
  positive: '#EAFFF6',
  negative: '#FFF1F2',
  neutral: '#8D8D8D',
  black: '#000000',
  green: '#14AF6E',
  red: '#EA1319',
  darkRed: '#9D2D2A',
  brown100: '#98845A',
  negativeRed: '#B60000',
  brand: {
    primary: '#786847',
    secondary: '#0A1E1D',
    tertiary: '#223C2E',
    foreground: '#343840',
    quaternary: '#616568',
    primary75: 'rgba(120, 104, 71, 0.75)',
    primary50: 'rgba(120, 104, 71, 0.50)',
    primary25: 'rgba(120, 104, 71, 0.25)',
    secondary75: 'rgba(10, 30, 29, 0.75)',
    secondary50: 'rgba(10, 30, 29, 0.5)',
    secondary25: 'rgba(10, 30, 29, 0.25)',
    tertiary75: 'rgba(34, 60, 46, 0.75)',
    tertiary50: 'rgba(34, 60, 46, 0.50)',
    tertiary25: 'rgba(34, 60, 46, 0.25)',
    highlight: '#FFE500',
    sgp: '#128152'
  },
  modals: {
    success: '#1BC47D',
    warning: '#FCB514',
    error: '#EA1319',
    expired: '#000000',
    neutral: '#DADADA'
  },
  buttons: {
    primary: '#128152',
    primaryContrast: '#ffffff',
    secondary: '#ffffff',
    alert: '#EA1319'
  },
  supporting: {
    primary: '#244F4D',
    secondary: '#128152',
    tertiary: '#786847',
    tertiarylight: '#98845A',
    accent100: '#E5E5E5',
    accent200: '#E5E5E5',
    accent300: '#E5E5E5',
    accent400: '#9AA1AF',
    accent500: '#7383a6',
    accent600: '#D9E5F6',
    accent700: '#F7FAFF',
    focusShadow: '#0075D4'
  },
  status: {
    error: '#EA1319',
    error50: '#EA131966',
    warning: '#FCB514',
    warning15: '#FCB51419',
    success: '#14AF6E',
    info: '#DADADA',
    expired: '#000000',
    unreachable: '#7a7f82',
    alert: '#fff6e3'
  },
  accountTypes: {
    all: '#25306A',
    writer: '#44d7b6',
    dok: '#0075D4',
    kiosk: '#7f34fc',
    safe: '#fb4281',
    cage: '#fa6400'
  },
  toggle: {
    enabledBackgroundColour: '#1BC47D',
    disabledBackgroundColour: '#DADADA',
    textColour: '#343840'
  },
  liveScore: '#3600D0',
  liveScoreAccent: '#FBF4FF',
  oddsBoost: '#00C785',
  border: '#aa8c57',
  cop: {
    selection: '#FFF8E1'
  },
  videowall: '#0C221D',
  price: '#128152',
  unavailable: '#DADADA'
};

const CAESARS: ITheme = {
  baseFontSize: '62.5%',
  colors,
  components: {
    ...shared.components,
    global: {
      textColor: colors.brand.foreground,
      overlay: colors.brand.secondary50,
      link: '#0075D4' // System-100 figma
    },
    buttons: {
      secondary: {
        borderColor: colors.brown100
      },
      tertiary: {
        background: colors.brand.primary,
        border: colors.brand.primary,
        color: colors.white
      }
    },
    header: {
      ...shared.components.header,
      background: colors.brand.secondary
    },
    login: {
      background: colors.brand.secondary
    },
    homePage: {
      background: 'linear-gradient(180deg, #1c2f2b 48.15%, #121212 100%)'
    },
    markets: {
      color: colors.brown100
    },
    navigation: {
      clock: '#fcb514',
      header: colors.brand.secondary,
      menu: {
        background: colors.brand.secondary,
        active: '#976F3C',
        activeBorder: '#244F4D'
      },
      tabs: {
        container: colors.brand.tertiary,
        item: colors.supporting.accent200
      },
      counter: {
        color: colors.white,
        background: colors.supporting.secondary,
        active: colors.brand.foreground
      },
      segmentedMenu: {
        selected: {
          color: colors.white,
          background: colors.supporting.primary
        }
      },
      competitionTabs: {
        selected: {
          color: colors.white,
          background: colors.supporting.primary
        }
      }
    },
    betslip: {
      tabs: {
        active: colors.supporting.primary
      },
      keypad: {
        background: colors.brand.secondary25
      },
      price: {
        fontColor: colors.supporting.secondary
      },
      teaser: {
        border: '#7CA18D',
        background: '#F4FCF2',
        selected: {
          color: colors.white,
          background: colors.supporting.primary
        }
      },
      footer: colors.brand.foreground
    },
    betWithCredits: {
      border: '#128152',
      text: '#128152',
      background: '#F4FCF2',
      stakeInput: '#0075D4',
      instructionalText: '#5A5E61'
    },
    cashOut: {
      betDetails: {
        bottomStripColor: colors.black
      },
      buttons: {
        primary: {
          backgroundColor: '#ccab67',
          disabled: {
            backgroundColor: colors.brand.primary
          }
        },
        secondary: {
          backgroundColor: '#128152',
          color: colors.white
        }
      }
    },
    sports: {
      accordion: {
        header: colors.brand.foreground
      },
      selections: {
        color: '#F4FCF2',
        borderColor: colors.supporting.primary,
        selected: {
          background: colors.supporting.primary,
          color: colors.white
        }
      }
    },
    rewards: {
      inactive: colors.white,
      background: colors.black,
      detailsMenu: {
        count: css`
          background-color: ${colors.darkRed};
          color: ${colors.white};
        `,
        inactive: css`
          border-bottom: 1px solid #5a5e61;
          color: #5a5e61;
        `,
        active: css`
          border-bottom: 4px solid ${colors.supporting.secondary};
          color: ${colors.brand.foreground};
          font-weight: 600;
        `
      }
    },

    posSecondary: {
      headerBackgroundColor: '#E5E5E5',
      priceColor: '#128152',
      shadowColor: colors.brand.secondary,
      totalsBackgroundColor: colors.brand.foreground
    },

    buyingPoints: {
      toggleBackground: colors.supporting.primary,
      toggleArrow: colors.supporting.primary,
      stroke: '#98845A'
    },
    mobile: {
      heading: {
        fontFamily: 'oswald, sans-serif'
      },
      tab: {
        selection: '#255E46',
        item: '#5A5E61'
      },
      scan: {
        background: '#2fb57d'
      }
    },
    images: {
      stroke: colors.supporting.primary
    },
    stake: {
      fontColor: '#0075D4',
      borderColor: '#0075D4'
    },
    trendingBetPrompt: {
      borderColour: colors.brand.sgp,
      backgroundColour: '#F4FCF2',
      selectionBackgroundColour: '#F4FCF2',
      ribbon: {
        textColour: colors.white,
        backgroundColour: colors.brand.sgp
      },
      leg: {
        selected: colors.brand.sgp,
        unselected: '#dadada',
        unselectedTextColor: '#343840'
      },
      price: {
        backgroundColor: colors.white,
        borderColor: '#7CA18D',
        priceTextColor: colors.supporting.primary,
        textColor: colors.neutral,
        payoutTextColor: colors.brand.sgp
      },
      button: {
        textColor: '#343840',
        borderColor: colors.brown100
      }
    },
    cannedParlays: {
      winningsExample: {
        base: css`
          color: #8d8d8d;
        `,
        selected: css`
          color: unset;
        `
      },
      priceButton: css`
        * {
          font-family: 'oswald', sans-serif;
        }
        border: 2px solid #244f4d;
      `,
      priceText: css`
        color: #244f4d;
      `,
      kioskSecondaryBackground: css`
        background-image: url(/images/mdmBannerImages/quick-pick-drop.webp);
        background-size: cover;
      `,
      kioskSecondaryLegDotsColour: colors.brand.sgp
    },
    parlayCards: {
      autoPick: {
        item: {
          selected: css`
            background-color: #244f4d;
            color: #ffffff;
          `
        },
        section: css`
          background-color: #244f4d;
        `
      }
    },
    notification: {
      background: colors.darkRed,
      color: colors.white,
      fontWeight: 'bold'
    },
    parlayBuilder: {
      header: css`
        background-color: #976f3c;
      `,
      item: {
        selected: css`
          border-left: 0.5rem solid #976f3c;
        `
      },
      subitem: {
        selected: css`
          ${shared.components.parlayBuilder.subitem.selected}
          background-color: #976f3c;
          color: ${colors.white};
        `
      }
    },
    bannerGradients: {
      0: '#343840',
      1: '#223C2E',
      2: '#000000',
      3: '#786847',
      4: '#244F4D'
    },
    comps: {
      iconColor: '#244F4D'
    },
    card: {
      header: css`
        background-color: ${colors.brand.foreground};
        color: ${colors.white};
      `
    },
    checkbox: {
      backgroundColour: '#F9F9F9',
      disabledColour: '#8D8D8D',
      checkedColour: '#0075D4'
    },
    enhancedSearch: {
      input: {
        background: colors.brand.secondary,
        textColor: colors.green
      },
      getStarted: {
        magnifyingGlass: colors.supporting.primary,
        clearPins: '#0075d4'
      }
    },
    keyboard: {
      background: 'linear-gradient(90deg, #0a1e1de8, #0c2621e8)',
      keys: '#555a58',
      buttons: '#e9ebea'
    },
    injuryProtection: {
      gradient: 'linear-gradient(90deg, #1B2C4D 0%, #236C7D 100%)',
      row: '#236C7D'
    },
    prizeDraw: {
      textColor: colors.brand.foreground
    }
  },
  secondaryBannerFonts: {
    small: '4.2rem',
    regular: '5.8rem',
    large: '7.4rem',
    xlarge: '10rem',
    topXl: '12.8rem'
  },
  borderRadius: {
    small: '2px',
    medium: '4px',
    large: '8px',
    xlarge: '10px',
    xxlarge: '20px',
    xxxlarge: '25px'
  },
  generic: {
    buttonHeight: '4.6rem',
    contextMenuHeight: '16.3rem',
    headerHeight: '9.4rem'
  },
  layers: {
    main: 100,
    betslip: 200,
    keypads: 300,
    topmenus: 400,
    tooltips: 500,
    modals: 600,
    timeoutWarning: 650,
    lock: 700,
    networkLoss: 800
  },
  shadows: {
    drop: '0 0 15px 0 rgba(1, 23, 46, 0.15)',
    veryheavy: '0 0 20px 0 rgba(0, 0, 0, 0.3)',
    heavy: '0 0 10px 0 rgba(9, 69, 189, 0.2)',
    light: '0 0 5px 0 rgba(18, 129, 82, 0.8)',
    lightred: '0 0 5px 0 rgba(208, 2, 27, 0.8)',
    lightFocus: '0 0 5px 0 rgba(0,117,212,1)'
  }
};

export default CAESARS;

export const brandLogoColors: Record<'caesars' | 'sportsbook', BrandLogoColorBackgroundSpecific> = {
  caesars: {
    onBackground: {
      dark: '#e8e3de',
      light: '#173432'
    }
  },
  sportsbook: {
    onBackground: {
      dark: '#cbaa65',
      light: '#876e3f'
    }
  }
} as const;
